import { observable, toJS, action } from 'mobx';
import { observer } from 'mobx-react';
import React, { PropTypes } from 'react';
import Modal from '../Modal';
import Form, { CheckBoxInput, ColorInput, DateInput, FormActions, FormBody, SelectInput, SwitchInput2, TextInput } from '../Form';
import Button from './Button';
import ReactDropzone from 'react-dropzone';
import CronIntervalInput from '../CronIntervalInput';
import FormMultiSelectInput from '../FormMultiSelectInput';

@observer
class ItemModify extends React.Component {
  static propTypes = {
    params: PropTypes.shape(),
    route: PropTypes.shape(),
    slug: PropTypes.string.isRequired,
    itemFields: PropTypes.array.isRequired
  }


  static contextTypes = {
    router: PropTypes.shape().isRequired,
    notify: PropTypes.shape().isRequired,
  }

  @observable title = '';
  @observable item = {};
  @observable multiSelectOptions = [];
  @observable editMode = false;

  componentWillMount() {
    const { title, store, id, params } = this.props;
    const itemId = id || params.id;
    this.editMode = itemId !== 'add';
    this.title = this.editMode ? `Edit ${title}` : `Add ${title}`;
    this.resetItem(itemId);
  }

  resetItem = (itemId) => {
    const { itemFields, isLoading, store } = this.props;
    if (this.editMode && (isLoading || store.isLoading)) {
      setTimeout(() => {
        this.resetItem(itemId);
      }, 100);
    } else {
      const foundItem = this.editMode ? store.findById(itemId) : null;
      const item = itemFields.filter(item => item.type !== 'derived').reduce((acc, { key, defaultValue }) => {
        acc[key] = !!foundItem && (!!foundItem[key] || foundItem[key] === false || foundItem[key] === 0) ? foundItem[key] : (!!defaultValue || defaultValue == 0 ? defaultValue : null);
        return acc;
      }, {});

      const multiSelectOptions = itemFields.filter(item => item.type === 'multi-select').reduce((acc, { key, options, fields }) => {
        acc[key] = options.map(option => ({
          id: option[fields.value],
          label: option[fields.label],
          value: !item[key] ? false : !!item[key].find(i => +(typeof i === 'object' ? i[fields.value] : i) === +option[fields.value]),
          ...(fields.others || []).reduce((acc, field) => Object.assign(acc, { [field]: option[field] }), {}),
        }));
        return acc;
      }, {});

      this.item = observable(item);
      this.multiSelectOptions = observable(multiSelectOptions);
    }
  }

  @action onDrop(files, key) {
    files[0].text().then(svgText => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svg = doc.getElementsByTagName("svg")[0];
      const s = new XMLSerializer();
      const str = s.serializeToString(svg);
      this.item[key] = str;
    }).catch(e => {
      const { notify } = this.context;
      notify.error({ message: 'Failed to add. Upload file in svg format.' });
    })
  }

  onChange = (e, key) => {
    const { itemFields } = this.props;
    this.item[key] = e.target.value
    const itemField = itemFields.find(item => item.key === key);
    if (!!itemField.propagateTo) {
      itemField.propagateTo.forEach(cKey => {
        const childField = itemFields.find(item => item.key === cKey);
        if (!!childField.resolveFromParent) this.onChange({ target: { value: childField.resolveFromParent(e.target.value) } }, cKey);
      })
    }
  };

  _renderField = ({ label, key, type, options, fields, readOnly, addOnly, editOnly, style, className, renderIcon, isLoading, filterOptions, resolve }) => {
    if (!this.editMode && editOnly) return null;
    if (this.editMode && addOnly) return null;

    switch (type) {
      case 'select':
        return <SelectInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} options={options} fields={fields} filterOptions={filterOptions} readOnly={readOnly} />
      case 'color':
        return <ColorInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} />
      case 'date':
        return <DateInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} />
      case 'datetime':
        return <DateInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} type="datetime-local" />
      case 'dropZone':
        return (
          <div key={key} className='icon-input'>
            <label>{label}</label>
            {renderIcon && renderIcon(this.item[key])}
            <ReactDropzone ref="dropzone"
              onDrop={(e) => this.onDrop(e, key)}
              className={className}
              style={style}
            />
          </div>
        );
      case 'cron-interval':
        return <CronIntervalInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} />;
      case 'checkbox':
        return <CheckBoxInput key={key} label={label} checked={this.item[key]} onChange={e => this.item[key] = e.target.checked} />
      case 'checkbox-numeric':
        return <CheckBoxInput key={key} label={label} checked={this.item[key]} onChange={e => this.item[key] = e.target.checked} numeric />
      case 'multi-select':
        return <FormMultiSelectInput id={`${key}-multi-select`} key={key} label={label} options={this.multiSelectOptions[key]} optionClicked={item => item.value = !item.value} isLoading={isLoading} filterOptions={filterOptions} />
      case 'derived':
        return <TextInput key={label} label={label} value={resolve(this.item[key])} readOnly />;
      case 'switch':
        return <SwitchInput2 key={label} label={label} value={!!+this.item[key]} onChange={(e) => this.item[key] = e.target.value} />;
      case 'switch-numeric':
        return <SwitchInput2 key={label} label={label} value={!!+this.item[key]} onChange={(e) => this.item[key] = e.target.value} numeric />;
      case 'password':
        return <TextInput type='password' key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} readOnly={readOnly} />;
      default:
        return <TextInput key={key} label={label} value={this.item[key]} onChange={e => this.onChange(e, key)} readOnly={readOnly} />;
    }
  }

  selectedOptionsReducer = options => options.reduce((acc, item) => {
    if (item.value === true) acc.push(item.id);
    return acc;
  }, []);

  handleSave = (e) => {
    e.preventDefault();
    const { store, slug, onSave, itemFields } = this.props;
    const { notify, router } = this.context;

    const payload = Object.assign(
      {},
      toJS(this.item),
      itemFields.filter(item => item.type === 'multi-select').reduce((acc, { key }) => {
        acc[key] = this.selectedOptionsReducer(this.multiSelectOptions[key]);
        return acc;
      }, {})
    );

    if (this.editMode) itemFields.filter(item => item.addOnly).forEach(item => delete payload[item.key]);
    if (!this.editMode) itemFields.filter(item => item.editMode).forEach(item => delete payload[item.key]);

    (this.editMode ? store.updateItem(payload) : store.createItem(payload))
      .then(() => {
        if (!!onSave) onSave();
        notify.success({ message: 'Success' });
        router.push(slug);
      })
      .catch(err => {
        console.log(err);
        notify.error(err);
      });
  }

  render() {
    const { isLoading, store, slug, itemFields } = this.props;

    return (
      <Modal title={this.title} slug={slug} isLoading={isLoading || store.isLoading} isSaving={store.isSaving} handleSave={this.handleSave}>
        <Form>
          <FormBody>
            {itemFields.map(field => this._renderField(field))}
              </FormBody>
          <FormActions>
            <Button label='Save' onClick={this.handleSave} isLoading={store.isSaving} />
          </FormActions>
        </Form>
      </Modal>
    );
  }
}

export default ItemModify;


// WEBPACK FOOTER //
// ./src/components/Form/ItemModify.js